import { Badge, Input, Select, Textarea } from '@/components/ui';
import { DbModelField } from '@/services/dbManager.service';
import { MessageSquare, Trash2, Type } from 'lucide-react';
import React, { useState } from 'react';

interface DbFormFieldProps {
  field: DbModelField;
  value: any;
  onChange: (value: any) => void;
  enumValues?: string[];
  disabled?: boolean;
}

export const DbFormField: React.FC<DbFormFieldProps> = React.memo(({ field, value, onChange, enumValues = [], disabled = false }) => {
  const isPk = field.isPrimary || field.isId;
  const isReadOnly = isPk || field.name === 'createdAt' || field.name === 'updatedAt' || disabled;
  const type = (field.type || '').toLowerCase();

  // Detección de Textarea para campos extensos
  const [isTextarea, setIsTextarea] = useState(
    type === 'string' &&
      (field.name.toLowerCase().includes('descripcion') ||
        field.name.toLowerCase().includes('comentario') ||
        field.name.toLowerCase().includes('observacion') ||
        field.name.toLowerCase().includes('mensaje') ||
        field.name.toLowerCase().includes('objetivo') ||
        field.name.toLowerCase().includes('recomendacion'))
  );

  const labelElement = (
    <div className="flex items-center gap-2 mb-1.5 w-full">
      <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
        {field.name} {field.isRequired && <span className="text-red-500 font-black ml-0.5">*</span>}
      </span>
      {isPk && (
        <Badge variant="blue" className="text-[8px] px-1.5 py-0 h-4 border border-blue-200">
          PK
        </Badge>
      )}
      {field.kind === 'enum' && (
        <Badge variant="yellow" className="text-[8px] px-1.5 py-0 h-4 border border-yellow-200">
          ENUM
        </Badge>
      )}
      {isReadOnly && !isPk && (
        <Badge variant="gray" className="text-[8px] px-1.5 py-0 h-4 border border-gray-200 italic opacity-70">
          READ ONLY
        </Badge>
      )}

      {/* Switcher para Input/Textarea */}
      {(type === 'string' || type === 'json' || type === 'text') && !isReadOnly && (
        <button
          onClick={() => setIsTextarea(!isTextarea)}
          type="button"
          title={isTextarea ? 'Cambiar a Input de línea única' : 'Cambiar a Área de texto'}
          className="ml-auto p-1.5 hover:bg-indigo-50 rounded-md text-indigo-400 hover:text-indigo-600 transition-colors bg-white border border-gray-100 shadow-sm flex items-center justify-center"
        >
          {isTextarea ? <Type size={14} /> : <MessageSquare size={14} />}
        </button>
      )}

      {!field.isRequired && !isPk && !isReadOnly && value !== null && (
        <button
          onClick={() => onChange(null)}
          title="Establecer como NULL"
          className={`${type !== 'string' ? 'ml-auto' : ''} text-[9px] font-bold text-gray-300 hover:text-red-500 flex items-center gap-1 transition-colors uppercase`}
        >
          <Trash2 size={10} /> Null
        </button>
      )}

      {value === null && !field.isRequired && (
        <span className="ml-auto text-[9px] font-black text-amber-500 uppercase italic">Es NULL</span>
      )}
    </div>
  );

  // 1. Manejo de ENUMS (Select)
  if (field.kind === 'enum' || (enumValues && enumValues.length > 0)) {
    return (
      <div className="space-y-1">
        {labelElement}
        <Select
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          options={enumValues.map((v) => ({ value: v, label: v }))}
          placeholder={`Seleccione ${field.type}...`}
          disabled={isReadOnly}
          className="h-11 font-bold text-sm bg-gray-50 border-gray-200 focus:bg-white transition-all"
        />
      </div>
    );
  }

  // 2. Manejo de BOOLEANS (Select simplificado)
  if (type === 'boolean') {
    return (
      <div className="space-y-1">
        {labelElement}
        <Select
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(e) => onChange(e.target.value === 'true')}
          options={[
            { value: 'true', label: 'SÍ / TRUE' },
            { value: 'false', label: 'NO / FALSE' },
          ]}
          placeholder="Seleccionar estado..."
          disabled={isReadOnly}
          className="h-11 font-bold text-sm bg-gray-50 border-gray-200"
        />
      </div>
    );
  }

  // 4. Manejo de NUMBERS y DATES
  const inputType = ['int', 'float', 'decimal'].includes(type) ? 'number' : type === 'datetime' ? 'datetime-local' : 'text';

  const formattedValue =
    type === 'datetime' && value
      ? (() => {
          const d = new Date(value);
          const tzOffset = d.getTimezoneOffset() * 60000;
          return new Date(d.getTime() - tzOffset).toISOString().slice(0, 16);
        })()
      : value === null || value === undefined
        ? ''
        : value;

  return (
    <div className={`space-y-1 ${isTextarea ? 'md:col-span-2' : ''}`}>
      {labelElement}
      {isTextarea ? (
        <Textarea
          value={formattedValue}
          onChange={(e) => onChange(e.target.value)}
          placeholder={`Ingresa ${field.name.toLowerCase()}...`}
          disabled={isReadOnly}
          className="min-h-32 font-medium text-sm bg-gray-50 border-gray-200 focus:bg-white transition-all scrollbar-thin"
        />
      ) : (
        <Input
          type={inputType}
          value={formattedValue}
          disabled={isReadOnly}
          onChange={(e) => {
            let finalVal: any = e.target.value;
            if (type === 'int') {
              finalVal = e.target.value === '' ? null : parseInt(e.target.value);
            } else if (['float', 'decimal'].includes(type)) {
              finalVal = e.target.value === '' ? null : parseFloat(e.target.value);
            } else if (type === 'datetime') {
              finalVal = e.target.value === '' ? null : new Date(e.target.value).toISOString();
            }
            onChange(finalVal);
          }}
          className={`h-11 font-bold text-sm transition-all ${
            isReadOnly
              ? 'bg-gray-100 border-gray-100 text-gray-400 opacity-60 cursor-not-allowed'
              : 'bg-gray-50 border-gray-200 focus:bg-white'
          }`}
        />
      )}
    </div>
  );
});
